import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Plus, X, Brain } from "lucide-react";
import { type TranscriptionData } from "@/redux/api/transcriptions.rtkq";
import { useGenerateSummaryMutation } from "@/redux/api/summaries.rtkq";
import { useGetTranscriptionsQuery } from "@/redux/api/transcriptions.rtkq";
import { toast } from "sonner";

interface SummaryGenerationFormProps {
  transcription: TranscriptionData;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSummaryGenerated?: (summaryId: string) => void;
}

const SummaryGenerationForm = ({ 
  transcription, 
  isOpen, 
  onOpenChange, 
  onSummaryGenerated 
}: SummaryGenerationFormProps) => {
  const [participants, setParticipants] = useState<string[]>([]);
  const [newParticipant, setNewParticipant] = useState("");
  const [generateSummary] = useGenerateSummaryMutation();
  const { refetch: refetchTranscriptions } = useGetTranscriptionsQuery({ userId: transcription.userId });

  // Get Q&A data directly from transcription
  const qaData = transcription.questionsFromAi || [];
  const questions = qaData.map(item => item.q);
  const answers = qaData.map(item => item.a);

  const handleAddParticipant = () => {
    if (newParticipant.trim()) {
      setParticipants([...participants, newParticipant.trim()]);
      setNewParticipant("");
    }
  };

  const handleRemoveParticipant = (index: number) => {
    setParticipants(participants.filter((_, i) => i !== index));
  };


  const handleGenerateSummary = async () => {
    // Close dialog immediately for better UX
    onOpenChange(false);
    
    // Reset form immediately
    setParticipants([]);
    
    try {
      const result = await generateSummary({
        transcriptionId: transcription.id,
        userId: transcription.userId,
        participants: participants.length > 0 ? participants : undefined,
        questions: questions.length > 0 ? questions : undefined,
        answers: answers.length > 0 ? answers : undefined,
      }).unwrap();

      toast.success("הסיכום נוצר בהצלחה!");
      await refetchTranscriptions(); // Refresh transcriptions to show the new summary
      onSummaryGenerated?.(result.text);
    } catch (error) {
      console.error("Error generating summary:", error);
      toast.error("שגיאה ביצירת הסיכום. נסה שוב.");
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset form when closing
    setParticipants([]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl flex flex-col h-[90vh] p-0">
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="text-xl font-semibold text-chen-900 text-right">
            יצירת סיכום לתמלול
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-right break-all">
            צור סיכום מפורט לתמלול: {transcription.fileName}
          </DialogDescription>
        </DialogHeader>
        <Separator />
        
        <div className="p-6 space-y-6 flex-1 overflow-y-auto">
          {/* Participants Section */}
          <div className="space-y-3">
            <Label className="text-right block font-medium">משתתפים (אופציונלי)</Label>
            <div className="space-y-2">
              {participants.map((participant, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="flex-1 text-right bg-muted px-3 py-2 rounded-md">
                    {participant}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveParticipant(index)}
                    className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  placeholder="שם משתתף"
                  value={newParticipant}
                  onChange={(e) => setNewParticipant(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleAddParticipant()}
                  className="text-right"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddParticipant}
                  disabled={!newParticipant.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Q&A Section */}
          {questions.length > 0 && (
            <div className="space-y-3">
              <Label className="text-right block font-medium">שאלות ותשובות מהתמלול</Label>
              <div className="space-y-3">
                {questions.map((question, index) => (
                  <div key={index} className="space-y-2 p-3 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground">שאלה:</span>
                      <span className="flex-1 text-right">{question}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground">תשובה:</span>
                      <span className="flex-1 text-right">{answers[index]}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        <Separator />
        
        {/* Actions */}
        <div className="p-6 flex justify-end gap-3">
          <Button variant="outline" onClick={handleClose}>
            ביטול
          </Button>
          <Button 
            onClick={handleGenerateSummary} 
            className="bg-chen-900 hover:bg-chen-800"
          >
            <Brain className="h-4 w-4 ml-2" />
            צור סיכום
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SummaryGenerationForm;
